import { Dictionary } from '@ngrx/entity';
import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, createSelector, emptyProps, on, props } from '@ngrx/store';
import { AppNotificationBase, JoiApp, JoiAppData, JoiAppInfo } from 'globals/store/app.data';
import { getDefaultProgramTabNames } from 'globals/store/share.data';
import { appsActions, LOAD_APP_NOTIFICATIONS , REMOVE_APP_NOTIFICATIONS } from '../../../store/app.era';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { MobileAppState } from './mobile.app.store';

export const LOAD_APP_ID = '[app] LOAD_APP_ID'
export const LOAD_APP = '[app] LOAD_APP'
export const LOAD_APP_INFO = '[app] LOAD_APP_INFO'
export const LOAD_APP_DATA = '[app] LOAD_APP_DATA'
export const LOAD_TIME = '[app] LOAD_TIME'
export const FLUTTER_INITIALIZED = '[app] FLUTTER_INITIALIZED (HOPEFULLY ENOUGH TIME)'
export const SHOW_NOTIFICATION = '[app] SHOW_NOTIFICATION'
export const CLOSE_NOTIFICATION = '[app] CLOSE_NOTIFICATION'
export const CLEAR_APP = '[app] CLEAR_APP'
export const SET_APP_NOT_FOUND = '[app] SET_APP_NOT_FOUND'

export interface IMobileApp {
  id: string
  appInfo: JoiAppInfo
  appData: JoiAppData
  app: JoiApp
  time: number
  shownNotification: AppNotificationBase  
  tabNames:{ [tId: string]: string } 
  initialized: boolean // time for flutter to initialize was given and failed OR appDatas already came back from flutter (even if empty)
  notifications: Dictionary<AppNotificationBase>
  notFound: boolean
}

const getDefaultMobileAppState = (): IMobileApp => ({
  id: null,
  appInfo: null,
  appData: null,
  app: null,
  time: new Date().getTime(),
  tabNames: getDefaultProgramTabNames(),
  initialized: false,
  notifications: {},
  notFound: false,
  shownNotification: null, /* {
    id: '123',
    appId: null,
    title: 'Notification title',
    message: 'Notification message',
    linkType: 'performers',
    linkUrl: null
  } */
})

export const MobileAppActions = createActionGroup({
  source: 'Mobile App',
  events: {
    'Load App Id': props<{ payload: string }>(),
    'Load App Info': props<{ payload: JoiAppInfo }>(),
    'Load App Data': props<{ payload: JoiAppData }>(),
    'Load App': props<{ payload: JoiApp }>(),
    'Flutter Initialized': emptyProps(),
    'Show Notification': props<{ payload: AppNotificationBase }>(),
    'Close Notification': emptyProps(),
    'Clear App': emptyProps(),
    'Load Time': emptyProps(),
    'Set App Not Found': props<{ payload: boolean }>(),
  } 
})

export const mobileAppReducer = createReducer(
  getDefaultMobileAppState(),
  on(MobileAppActions.loadAppId, (state, { payload }) => ({ ...state, id: payload })),
  on(MobileAppActions.loadAppInfo, (state, { payload }) => ({ ...state, appInfo: { ...payload } })),
  on(MobileAppActions.loadAppData, (state, { payload }) => ({ ...state, appData: { ...payload } })),
  on(MobileAppActions.loadApp, (state, { payload }) => ({ ...state, app: payload, tabNames: payload?.eventData?.tabNames || getDefaultProgramTabNames() })),
  on(MobileAppActions.flutterInitialized, (state) => ({ ...state, initialized: true })),
  on(MobileAppActions.showNotification, (state, { payload }) => ({ ...state, shownNotification: payload })),
  on(MobileAppActions.closeNotification, (state) => ({ ...state, shownNotification: null })),
  on(MobileAppActions.clearApp, (state) => ({ ...getDefaultMobileAppState(), time: state.time, initialized: state.initialized })),
  on(MobileAppActions.setAppNotFound, (state, { payload }) => ({ ...state, notFound: payload })),
  on(appsActions.loadAppNotifications, (state, { notifications }) => {
    const stateNotifications = jsonCopyObject(state.notifications) as Dictionary<AppNotificationBase>
    notifications.forEach(n => stateNotifications[n.id] = n)
    return { ...state, notifications: stateNotifications }
  }),
  on(MobileAppActions.loadTime, (state) => ({ ...state, time: new Date().getTime() })),
  on(appsActions.removeAppNotifications, (state, { notifications }) => {
    const stateNotifications = jsonCopyObject(state.notifications) as Dictionary<AppNotificationBase>
    notifications.forEach(n => delete stateNotifications[n.id])
    return { ...state, notifications: stateNotifications}
  }),
)

export const mobileAppFeature = createFeature({
  name: 'mobileApp',
  reducer: mobileAppReducer,
  extraSelectors: ({ selectMobileAppState }) => ({
    selectState: createSelector(selectMobileAppState, state => state)
  })
})

export const mobileAppStateSelector = createFeatureSelector<IMobileApp>(mobileAppFeature.name)
